/**
 * StudySync Authentication Service
 * Handles Firebase authentication and study verification
 */

import { api, authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import type { StudyVerificationResult, ApiResponse } from '../types';

export class AuthService {
  /**
   * Verify study code
   */
  static async verifyStudyCode(
    studyCode: string
  ): Promise<ApiResponse<StudyVerificationResult>> {
    return api.post(API_ENDPOINTS.AUTH.VERIFY_STUDY, { studyCode });
  }

  /**
   * Notify researcher about participant enrollment
   */
  static async notifyResearcher(
    studyCode: string,
    participantEmail: string,
    message?: string
  ): Promise<ApiResponse> {
    return api.post(API_ENDPOINTS.AUTH.NOTIFY_RESEARCHER, {
      studyCode,
      participantEmail,
      message,
    });
  }

  /**
   * Get researcher studies (authenticated)
   */
  static async getResearcherStudies(
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.get(API_ENDPOINTS.AUTH.RESEARCHER_STUDIES, token);
  }

  /**
   * Create new study (authenticated)
   */
  static async createStudy(
    studyData: {
      name: string;
      description?: string;
      duration?: string;
      password?: string;
      collaborators?: string[];
      settings?: {
        allowSelfEnrollment?: boolean;
        requireVerification?: boolean;
        dataRetentionDays?: number;
      };
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(API_ENDPOINTS.AUTH.CREATE_STUDY, studyData, token);
  }

  /**
   * Update study (authenticated)
   */
  static async updateStudy(
    studyId: string,
    studyData: {
      name?: string;
      description?: string;
      duration?: string;
      password?: string;
      status?: 'active' | 'inactive';
      settings?: {
        allowSelfEnrollment?: boolean;
        requireVerification?: boolean;
        dataRetentionDays?: number;
      };
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.put(
      API_ENDPOINTS.AUTH.UPDATE_STUDY(studyId),
      studyData,
      token
    );
  }

  /**
   * Delete study (authenticated)
   */
  static async deleteStudy(
    studyId: string,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.delete(API_ENDPOINTS.AUTH.DELETE_STUDY(studyId), token);
  }

  /**
   * Join study as collaborator (authenticated)
   */
  static async joinStudy(
    joinData: {
      studyCode: string;
      password: string;
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(API_ENDPOINTS.AUTH.JOIN_STUDY, joinData, token);
  }

  /**
   * Get collaborative studies (authenticated)
   */
  static async getCollaborativeStudies(
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.get(API_ENDPOINTS.AUTH.COLLABORATIVE_STUDIES, token);
  }

  /**
   * Validate study code format
   */
  static validateStudyCode(studyCode: string): boolean {
    // Study codes should be 6-8 characters, alphanumeric uppercase
    const studyCodeRegex = /^[A-Z0-9]{6,8}$/;
    return studyCodeRegex.test(studyCode);
  }

  /**
   * Format study code (uppercase, remove spaces)
   */
  static formatStudyCode(studyCode: string): string {
    return studyCode.replace(/\s/g, '').toUpperCase();
  }
}

export default AuthService;
